/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/
import { Point } from 'geojson';
import { VisionApplication } from '../application';
import { IPubSubSubscriber, PubSubManager } from '../pubsub';
import { BaseService } from './base';

interface AssetTelemetryCollection {
    updateTime: number;
    groupId: string;
    updateInformation: AssetTelemetry[];
}

export interface AddressInformation {
    housenumber: string | null;
    addressStreet: string | null;
    roadStreet: string | null;
    maxspeedKmh: number | null;
    assumedSpeed: boolean | null;
    city: string | null;
    postcode: string | null;
    region: string | null;
    addressDistance: number | null;
    roadDistance: number | null;
}

/** Defines spreader telemetry coming from the server.
 *
 */
export class SpreaderTelemetry {
    solidMode!: number;
    prewetMode!: number;
    antiiceMode!: number;

    solidSetRate!: number;
    prewetSetRate!: number;
    antiiceSetRate!: number;
    solidActualRate!: number;
    prewetActualRate!: number;
    antiiceActualRate!: number;

    solidMaterial!: string;
    prewetMaterial!: string;
    antiiceMaterial!: string;

    airTemp!: number;
    roadTemp!: number;

    solidTripTotal!: number;
    prewetTripTotal!: number;
    antiiceTripTotal!: number;

    seasonalSolidTotal1!: number;
    seasonalSolidTotal2!: number;
    seasonalSolidTotal3!: number;
    seasonalSolidTotal4!: number;
    seasonalPrewetTotal1!: number;
    seasonalPrewetTotal2!: number;
    seasonalPrewetTotal3!: number;
    seasonalPrewetTotal4!: number;
    seasonalAntiiceTotal1!: number;
    seasonalAntiiceTotal2!: number;
    seasonalAntiiceTotal3!: number;
    seasonalAntiiceTotal4!: number;
}

export class AssetTelemetry {
    id!: string;
    name!: string;
    icon!: string;
    position!: Point;
    heading!: number;
    speed!: number;
    odometer!: number;
    hours!: number;
    ignition!: boolean;
    state!: string;
    front!: string | null;
    top!: string | null;
    back!: string | null;
    bottom!: string | null;
    lastSeen!: number;
    input1!: boolean;
    i1!: string;
    input2!: boolean;
    i2!: string;
    input3!: boolean;
    i3!: string;
    input4!: boolean;
    i4!: string;
    b!: number; // beacon count;
    cameraImage!: string;

    analog1!: number;
    analog2!: number;
    analog3!: number;
    analog4!: number;

    r!: boolean; // Rocking
    g!: boolean; // Grading
    m!: boolean; // Mowing
    s!: boolean; // Spreading
    p!: boolean; // Plowing

    spreader!: SpreaderTelemetry;
    //address!: AddressInformation; // Too slow for the moment,  revisit after.

    address!: string | null;
    postedSpeed!: number | null;
}

export class LatestTelemetryService extends BaseService implements IPubSubSubscriber {
    identifier: string;
    assetCache!: Map<string, AssetTelemetry>;
    currentFolderId: string | undefined | null;
    lastUpdateTime!: number;
    pubsub: PubSubManager;

    constructor(app: VisionApplication) {
        super();

        this.identifier = 'cc64c5da-9488-4e05-b5c8-e3cc77272a00';
        this.assetCache = new Map<string, AssetTelemetry>();
        this.currentFolderId = undefined;
        this.lastUpdateTime = 0;
        this.pubsub = app.pubsub;

        app.pubsub.subscribe('FOLDER_SWITCH', this);
    }

    public async init() {
        return new Promise((resolve, _reject) => {
            console.log('Telemetry Service Loaded');
            resolve('Loaded');
        });
    }

    public tick() {
        this.processAssetTelemetry();
        setInterval(() => {
            this.processAssetTelemetry();
        }, 20000);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    notify(msg: string, data?: any): void {
        //console.log(msg);

        if (msg == 'FOLDER_SWITCH') {
            //console.log('Processing');
            this.assetCache.clear();
            this.currentFolderId = data;
            this.lastUpdateTime = 0;
            this.processAssetTelemetry(true);
        }
    }

    public processAssetTelemetry(zoom = false) {
        if (this.currentFolderId == undefined || this.currentFolderId == null) {
            //console.log('No folder selected yet');
            return;
        }
        const request = this.getAssetTelemetry();

        request.then((data) => {
            //console.log(data.parsedBody);
            if (data.parsedBody) {
                if (this.currentFolderId != data.parsedBody.groupId) {
                    return;
                }
                this.lastUpdateTime = data.parsedBody?.updateTime;
                data.parsedBody.updateInformation.forEach((item) => {
                    this.assetCache.set(item.id, item);
                });

                const notification = [];
                notification[0] = data.parsedBody.updateInformation;
                notification[1] = zoom;
                this.pubsub.publish('UPDATE_ASSET_TELEMETRY', notification);
            }
        });
    }

    private async getAssetTelemetry() {
        this.lastUpdateTime = 0;
        this.lastUpdateTime = this.lastUpdateTime - 500;
        const response = await this.get<AssetTelemetryCollection>(this.url + '/api/' + this.orgid + '/telemetry/latest/folder/' + this.currentFolderId + '?timestamp=' + this.lastUpdateTime);
        return response;
    }

    public async getAssetTelemetryForFolder(folderId: string) {
        this.lastUpdateTime = 0;
        this.lastUpdateTime = this.lastUpdateTime - 500;
        const response = await this.get<AssetTelemetryCollection>(this.url + '/api/' + this.orgid + '/telemetry/latest/folder/' + folderId + '?timestamp=' + this.lastUpdateTime);
        return response;
    }

    public static getDigitalInput(data: AssetTelemetry, num: number): boolean | undefined {
        switch (num) {
            case 1:
                return data.input1;
            case 2:
                return data.input2;
            case 3:
                return data.input3;
            case 4:
                return data.input4;
        }
    }

    public static getAnalogInput(data: AssetTelemetry, num: number): number | undefined {
        switch (num) {
            case 1:
                return data.analog1;
            case 2:
                return data.analog2;
            case 3:
                return data.analog3;
            case 4:
                return data.analog4;
        }
    }

    public async getLatestForAsset(assetId: string): Promise<AssetTelemetry | undefined> {
        const url = this.url + '/api/' + this.orgid + '/telemetry/latest/asset/' + assetId;
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }
}
